import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { Tag } from './tags';

export interface Track {
  id: number;
  track_id: string;
  code: string;
  name: string;
  state: string;
  type: string;
  display_name: string | null;
  alternative_name: string | null;
  track_conditions_name: string | null;
  track_summary: string | null;
  track_summary_firm: string | null;
  track_summary_good: string | null;
  track_summary_soft: string | null;
  track_summary_heavy: string | null;
  track_summary_updated_at: string | null;
  track_notes: string | null;
  created_at: string;
  updated_at: string;
  tags: Tag[];
}

export type TrackDetailsInput = Pick<
  Track,
  | 'track_id'
  | 'code'
  | 'name'
  | 'state'
  | 'type'
  | 'display_name'
  | 'alternative_name'
  | 'track_conditions_name'
  | 'track_summary'
  | 'track_summary_firm'
  | 'track_summary_good'
  | 'track_summary_soft'
  | 'track_summary_heavy'
  | 'track_notes'
>;

export function useTracks() {
  return useQuery({
    queryKey: ['tracks'],
    queryFn: () => apiClient<Track[]>('/tracks'),
  });
}

export function useUpdateTrackTags() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ trackId, tagIds }: { trackId: string; tagIds: number[] }) =>
      apiClient<Track>(`/tracks/${trackId}`, { method: 'PUT', body: JSON.stringify({ tag_ids: tagIds }) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tracks'] }),
  });
}

export function useUpdateTrackDetails() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ trackId, details }: { trackId: string; details: TrackDetailsInput }) =>
      apiClient<Track>(`/tracks/${trackId}/details`, { method: 'PUT', body: JSON.stringify(details) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tracks'] }),
  });
}
